import { IconButton, Tooltip } from '@terra/ui-design-system';
import { useTranslations } from 'next-intl';
import {
  formatTokenCount,
  type TokenUsageEstimate,
} from './tokenUsageEstimate';

const RING_SIZE = 16;
const RING_STROKE_WIDTH = 2;
const RING_RADIUS = (RING_SIZE - RING_STROKE_WIDTH) / 2;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

export function ContextUsageRing({
  estimate,
  isOpen,
  onToggle,
}: {
  estimate: TokenUsageEstimate;
  isOpen: boolean;
  onToggle: () => void;
}) {
  const t = useTranslations('chat.copilot.tokenUsage');
  const usedFraction = Math.min(Math.max(estimate.usedFraction, 0), 1);
  const percent = Math.round(usedFraction * 100);
  const arcLength = usedFraction * RING_CIRCUMFERENCE;
  const progressColor = estimate.isOverTrigger
    ? 'var(--colors-fg-error)'
    : 'currentColor';
  const trackColor = 'var(--colors-border-default)';
  const tooltipTitle = t('contextTooltip', {
    percent,
    used: formatTokenCount(estimate.orchestratorTokens),
    window: formatTokenCount(estimate.effectiveWindowTokens),
  });

  return (
    <Tooltip
      title={tooltipTitle}
      positioning={{ strategy: 'fixed', placement: 'top' }}
    >
      <IconButton
        type="button"
        variant="ghost"
        size="xs"
        aria-label={t('contextUsageAriaLabel', { percent })}
        aria-pressed={isOpen}
        onClick={onToggle}
        color={isOpen ? 'fg.default' : 'fg.muted'}
        _hover={{ color: 'fg.default', bg: 'transparent' }}
      >
        <svg
          width={RING_SIZE}
          height={RING_SIZE}
          viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
          role="img"
          aria-hidden="true"
        >
          <circle
            cx={RING_SIZE / 2}
            cy={RING_SIZE / 2}
            r={RING_RADIUS}
            fill="none"
            stroke={trackColor}
            strokeWidth={RING_STROKE_WIDTH}
          />
          <circle
            cx={RING_SIZE / 2}
            cy={RING_SIZE / 2}
            r={RING_RADIUS}
            fill="none"
            stroke={progressColor}
            strokeWidth={RING_STROKE_WIDTH}
            strokeLinecap="round"
            strokeDasharray={`${arcLength} ${RING_CIRCUMFERENCE}`}
            transform={`rotate(-90 ${RING_SIZE / 2} ${RING_SIZE / 2})`}
            style={{
              transition: 'stroke-dasharray 0.4s ease, stroke 0.2s ease',
            }}
          />
        </svg>
      </IconButton>
    </Tooltip>
  );
}
